<template>
  <div id="github_accounts" class="flex flex-col gap-10">
    <div class="flex flex-col gap-6">
      <div
        id="github_accounts_header"
        class="flex flex-row justify-between items-center"
      >
        <h1 class="text-xl font-semibold text_color">Github Accounts</h1>

        <a-button class="primary_medium" @click="handleConnectGithub">
          Install GitHub App
        </a-button>
      </div>

      <div class="w-full">
        <div
          v-if="fetchError"
          class="flex flex-col items-center justify-center gap-3 py-12 rounded-lg border_lg"
        >
          <EmptyScreen
            source="accounts-error"
            text="Failed to load connected accounts"
            padding="my-0"
          />
        </div>

        <SimpleTable
          v-else
          :headers="accountsTableHeaders"
          :data="accounts"
          :show-actions-column="true"
          :actions="tableActions"
          :loading="loadingAccounts"
          disable-horizontal-scroll
          :custom-column-render="customColumnRender"
          :table-props="{ virtual: false, maxHeight: undefined }"
        />
      </div>
    </div>

    <ConfirmationModal
      v-model:visible="deleteModalVisible"
      title="Delete GitHub Account"
      :message="`Are you sure you want to disconnect '${deletingAccount?.account_id}'? This action cannot be undone.`"
      :loading="isDeleting"
      type="delete"
      confirm-text="Delete"
      loading-text="Deleting..."
      @confirm="handleDeleteConfirm"
      @cancel="deleteModalVisible = false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { message } from "ant-design-vue";
import { useGithubCallback } from "@/src/composables/useGithubCallback.js";
import {
  accountsTableHeaders,
  accountColumnKeys,
  githubPopup,
} from "@/src/constants/codeReviewerConstants.js";
import CodeService from "@/src/api/CodeService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import AccountCell from "@/src/components/code_reviewer/AccountCell.vue";
import SimpleTable from "@/src/components/custom_table_components/SimpleTable.vue";

const accounts = ref([]);
const loadingAccounts = ref(false);
const fetchError = ref(false);
const deleteModalVisible = ref(false);
const deletingAccount = ref(null);
const isDeleting = ref(false);
const togglingId = ref(null);
const popupCheckTimer = ref(null);

const clearPopupCheck = () => {
  if (popupCheckTimer.value) {
    clearInterval(popupCheckTimer.value);
    popupCheckTimer.value = null;
  }
};

const watchPopup = (popup) => {
  clearPopupCheck();
  popupCheckTimer.value = setInterval(() => {
    if (popup.closed) {
      clearPopupCheck();
      fetchAccounts();
    }
  }, 500);
};

const makeColumnRender = (columnKey) => ({
  component: AccountCell,
  props: (record) => ({ column: columnKey }),
});

const customColumnRender = {
  [accountColumnKeys.ACCOUNT_ID]: makeColumnRender(accountColumnKeys.ACCOUNT_ID),
  [accountColumnKeys.STATUS]: makeColumnRender(accountColumnKeys.STATUS),
  [accountColumnKeys.REPO_COUNT]: makeColumnRender(accountColumnKeys.REPO_COUNT),
  [accountColumnKeys.IS_PRIMARY]: makeColumnRender(accountColumnKeys.IS_PRIMARY),
};

const tableActions = [
  {
    label: "Set as Primary",
    handler: (record) => handleSetPrimary(record),
    condition: (record) => !record.is_primary,
  },
  {
    label: "Suspend",
    handler: (record) => handleToggleActive(record, false),
    condition: (record) =>
      record.is_active !== false && togglingId.value !== record.id,
  },
  {
    label: "Unsuspend",
    handler: (record) => handleToggleActive(record, true),
    condition: (record) =>
      record.is_active === false && togglingId.value !== record.id,
  },
  {
    label: "Delete",
    handler: (record) => handleDisconnect(record),
  },
];

const fetchAccounts = async () => {
  loadingAccounts.value = true;
  fetchError.value = false;
  try {
    const data = await CodeService.reviewer.integrations.list();
    accounts.value = data?.integrations ?? [];
  } catch (error) {
    console.error("Failed to fetch connected accounts:", error);
    fetchError.value = true;
  } finally {
    loadingAccounts.value = false;
  }
};

useGithubCallback({
  onSuccess: () => {
    message.success("GitHub App installed successfully");
    fetchAccounts();
  },
  onError: (reason) => message.error(reason),
});

const handleConnectGithub = async () => {
  try {
    const data = await CodeService.reviewer.install.githubUrl();
    const installUrl = data?.install_url;
    if (!installUrl) {
      message.error("Failed to get GitHub App install URL");
      return;
    }

    const popup = window.open(
      installUrl,
      githubPopup.NAME,
      githubPopup.OPTIONS,
    );
    if (!popup) {
      message.error("Popup was blocked. Please allow popups and try again.");
    } else {
      watchPopup(popup);
    }
  } catch (error) {
    console.error("Failed to get GitHub App install URL:", error);
    message.error("Failed to connect to GitHub");
  }
};

const handleSetPrimary = async (account) => {
  try {
    await CodeService.reviewer.integrations.setPrimary(account.id);
    message.success("Primary integration updated");
    fetchAccounts();
  } catch (error) {
    console.error("Failed to set primary integration:", error);
    message.error("Failed to set primary integration");
  }
};

const handleToggleActive = async (account, isActive) => {
  if (togglingId.value) return;
  togglingId.value = account.id;
  try {
    await CodeService.reviewer.integrations.update(account.id, {
      is_active: isActive,
    });
    message.success(isActive ? "Integration unsuspended" : "Integration suspended");
    fetchAccounts();
  } catch (error) {
    console.error("Failed to update integration:", error);
    message.error("Failed to update integration");
  } finally {
    togglingId.value = null;
  }
};

const handleDisconnect = (account) => {
  deletingAccount.value = account;
  deleteModalVisible.value = true;
};

const handleDeleteConfirm = async () => {
  if (!deletingAccount.value) return;
  isDeleting.value = true;
  try {
    await CodeService.reviewer.integrations.delete(deletingAccount.value.id);
    message.success("GitHub account disconnected successfully");
    deleteModalVisible.value = false;
    deletingAccount.value = null;
    fetchAccounts();
  } catch (error) {
    console.error("Failed to disconnect account:", error);
    message.error("Failed to disconnect account");
  } finally {
    isDeleting.value = false;
  }
};

onMounted(() => {
  fetchAccounts();
});

onUnmounted(() => clearPopupCheck());
</script>
